if (process.env.NODE_ENV === 'development') {
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  const ReactRedux = require('react-redux');
  whyDidYouRender(React, {
    trackAllPureComponents: true,
    trackExtraHooks: [[ReactRedux, 'useSelector']],
  });
}
import React, {StrictMode, useEffect} from 'react';
import * as Sentry from '@sentry/react';
import {Provider} from 'react-redux';
import makeStore from 'app/services/store';
import createHistory from 'app/services/history';
import ReactDOM from 'react-dom';
import {loadableReady} from '@loadable/component';
import {fetchSelectedDistrict, setHydrationFinished} from 'app/actions/sessionActions';
import {AppShell} from 'app/components/AppShell';
import {HelmetProvider} from 'react-helmet-async';
import {ThemeProvider} from 'styled-components';
import * as media from 'app/styles/media';
import * as cssConstants from 'app/styles/cssConstants';
import {StyledEngineProvider} from '@mui/material';
import {ThemeProvider as MuiThemeProvider} from '@mui/material';
import materialTheme from 'app/styles/materialTheme';
import {useConstructor} from 'app/hooks/useConstructor';
import {destroyParallax} from 'app/components/ads/useParallax';
// import {reportWebVitals} from 'app/helpers/reportWebVitals';
import {isTemplateBlacklistedForAdRefresh, destroySoDSkin} from 'app/components/ads/adHelpers';
import createEmotionCache from 'app/styles/createEmotionCache';
import {CacheProvider} from '@emotion/react';
import routes from 'app/components/csrRoutesConfig';
import {destroyLazyLoadListener} from 'app/components/ads/lazyLoadHelpers';
import {checkCurrentAppRevision} from 'app/services/getAppRevision';
import {windowIsDefined} from 'app/helpers/windowIsDefined';
import {Router} from 'react-router';
import {LicenseInfo} from '@mui/x-license-pro';
import {NativeProvider} from 'app/contexts/NativeContext';
import {isNativeFlutterApp} from 'app/helpers/nativeHelpers';
import {ComponentErrorHandler} from 'app/components/error/ComponentErrorHandler';
import {measureHydrationDuration} from 'app/services/performance/PerformanceMeasurments';
import {markHydrationCompleted} from 'app/services/performance/PerformanceMarks';
import usePerformanceMeasurement from 'app/services/performance/usePerformanceMeasurement';
import PostMessageEventName from 'app/types/native/PostMessageEventName';
import {captureSentryExceptionAndThrowLocal} from 'app/services/sentry/sentryLogging';
import processHydrationErrors from 'app/services/sentry/hydrationErrorQueueHandler';
import initSentry from 'app/services/sentry/initSentry';
import {LayoutProvider} from 'app/contexts/LayoutContext';

const history = createHistory();
let root = '/';
if (windowIsDefined) {
  if (window.native) {
    try {
      window.native.postMessage(PostMessageEventName.HydrationFinished);
    } catch (error) {
      captureSentryExceptionAndThrowLocal(error);
    }
  }
  root = window.location.pathname;
  LicenseInfo.setLicenseKey(process.env.MUI_X_PRO_LICENCE_KEY);
}

const store = makeStore(root);
const cache = createEmotionCache();

const App = () => {
  const location = history.location;
  // Handler of Router would be registered first and execute first
  useConstructor(() => {
    // Need to run before rendering
    if (isNativeFlutterApp()) {
      window.handleNativeResume = () => {};
    }
    let prevLocation = location;

    history.listen(location => {
      let isMatchPage, isBlacklistedTemplate;
      if (prevLocation) {
        // Prevent execution of destroySlots for location change:
        // From BestPlayerVoting: `/lineup?dialog=best-player-voting` To MatchPage: `/lineup`
        // From MatchPage: `/match/:slug` To MatchPage: `/match/:slug/(info|lineup|standing|duels)
        const prevMatchPage = prevLocation.pathname.startsWith('/match/');
        const currentMatchPage = location.pathname.startsWith('/match/');
        isMatchPage = prevMatchPage && currentMatchPage;

        // Prevent execution of destroySlots for location change without template change
        // From: `/select` To: `/select/bayern`
        // From: `/region/:slug/competitions` to `/region/:slug/competitions/:cat/ageGroup/:slug`
        const currentTemplate = isTemplateBlacklistedForAdRefresh(location.pathname);
        const prevTemplate = isTemplateBlacklistedForAdRefresh(prevLocation.pathname);
        isBlacklistedTemplate = currentTemplate.blacklisted && prevTemplate.template === currentTemplate.template;
      }

      prevLocation = location;
      if (location.hash || isMatchPage || isBlacklistedTemplate) {
        // Prevent execution of destroySlots for location change not based on pathname for:
        // - location.hash:  /photos/:slug#3
        // - isMatchLineupVoting: /match/:matchSlug/lineup?dialog=best-player-voting (direct)
        return;
      }
      destroySoDSkin();
      destroyLazyLoadListener();
      destroyParallax();
    });
  });

  // Fetch initial district data, if only slug is set in redux
  useEffect(() => {
    const districtStore = store.getState().session?.district;
    if (!districtStore.id && districtStore.slug && !location.pathname.includes('/region/')) {
      fetchSelectedDistrict(districtStore.slug, undefined, location.pathname)(store.dispatch, store.getState);
    }
  }, []);

  useEffect(() => {
    // Need to run after rendering
    history.listen((_, action) => {
      // Reset Scroll-Position when location is changed, but not when using back-and-forth-buttons
      // https://reacttraining.com/react-router/web/guides/scroll-restoration
      if (action === 'PUSH') {
        window.scrollTo(0, 0);
      }
      console.log('history listen...');
    });
  }, []);

  useEffect(() => {
    const visibilityChangeListener = async () => {
      if (document.visibilityState === 'visible') {
        checkCurrentAppRevision();
      }
    };
    document.addEventListener('visibilitychange', visibilityChangeListener);
    return () => {
      document.removeEventListener('visibilitychange', visibilityChangeListener);
    };
  }, []);

  usePerformanceMeasurement();

  return (
    <Provider store={store}>
      <div id='app'>
        <Router history={history}>
          <HelmetProvider>
            <StyledEngineProvider injectFirst>
              <CacheProvider value={cache}>
                <NativeProvider>
                  <MuiThemeProvider theme={materialTheme}>
                    <ThemeProvider theme={{...media, ...cssConstants}}>
                      <Sentry.ErrorBoundary
                        fallback={<ComponentErrorHandler />}
                        beforeCapture={scope => {
                          scope.setLevel('fatal');
                          scope.setTag('type', 'component');
                        }}>
                        <LayoutProvider>
                          <AppShell routes={routes} />
                        </LayoutProvider>
                      </Sentry.ErrorBoundary>
                    </ThemeProvider>
                  </MuiThemeProvider>
                </NativeProvider>
              </CacheProvider>
            </StyledEngineProvider>
          </HelmetProvider>
        </Router>
      </div>
    </Provider>
  );
};

const onComponentIsHydrated = () => {
  store.dispatch(setHydrationFinished());
  markHydrationCompleted();
  measureHydrationDuration();
};

loadableReady(() => {
  initSentry();
  processHydrationErrors();

  ReactDOM.hydrate(
    <StrictMode>
      <App />
    </StrictMode>,
    document.getElementById('root'),
    onComponentIsHydrated
  );
});
// FIXME: reportWebVitals() causes Memory Leak
// reportWebVitals();

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then(registration => {
        console.log('SW registered: ', registration);
      })
      .catch(registrationError => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}
